<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { getAuthInstance } from '@/firebase/app'
import { getAuthErrorHandler } from '@/utils/authErrorHandler'

const props = defineProps({
  // Check token expiry every N minutes (default: 5 minutes)
  checkIntervalMinutes: {
    type: Number,
    default: 5
  },
  // Refresh token when it expires in N minutes (default: 10 minutes)
  refreshThresholdMinutes: {
    type: Number,
    default: 10
  }
})

const authStore = useAuthStore()
const tokenCheckInterval = ref(null)
const isActive = ref(false)

const checkTokenExpiry = async () => {
  if (!authStore.isLoggedIn || !isActive.value) return

  try {
    const user = getAuthInstance().currentUser
    if (!user) {
      stopMonitoring()
      return
    }

    // Get the token claims to check expiry
    const tokenResult = await user.getIdTokenResult()
    const expirationTime = new Date(tokenResult.expirationTime)
    const now = new Date()
    const minutesUntilExpiry = (expirationTime - now) / (1000 * 60)

    console.debug(`Token expires in ${Math.round(minutesUntilExpiry)} minutes`)

    // If token expires within threshold, refresh it proactively
    if (minutesUntilExpiry <= props.refreshThresholdMinutes) {
      console.log('Proactively refreshing token...')
      await authStore.refreshToken()
    }
  } catch (error) {
    console.warn('Error checking token expiry:', error)
    // If we can't check or refresh the token, let the auth error handler deal with it
    const authHandler = getAuthErrorHandler()
    await authHandler.handleAuthError(error, 'Token monitoring')
  }
}

const startMonitoring = () => {
  if (tokenCheckInterval.value || !authStore.isLoggedIn) return

  console.log('Starting auth token monitoring')
  isActive.value = true

  tokenCheckInterval.value = setInterval(checkTokenExpiry, props.checkIntervalMinutes * 60 * 1000)

  // Do an immediate check
  checkTokenExpiry()
}

const stopMonitoring = () => {
  if (tokenCheckInterval.value) {
    console.log('Stopping auth token monitoring')
    clearInterval(tokenCheckInterval.value)
    tokenCheckInterval.value = null
  }
  isActive.value = false
}

// Watch auth state changes to start/stop monitoring
watch(
  () => authStore.isLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn) {
      startMonitoring()
    } else {
      stopMonitoring()
    }
  },
  { immediate: true }
)

onMounted(() => {
  // Monitoring will start automatically via auth state watcher if user is logged in
})

onUnmounted(() => {
  stopMonitoring()
})
</script>

<template>
  <span style="display: none" aria-hidden="true"></span>
</template>
